import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  KanbanStageLabel,
  Page,
  PageHeader,
  Table,
  cardClasses,
  cn,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type { PhaseDeadline } from '@evogard/contract';
import {
  MAX_DEADLINE_HOURS,
  PERIPHERAL_STAGE_COLORS,
  SLA_DAY_KINDS,
  SLA_DAY_KIND_LABEL,
  SLA_DEPARTMENTS,
  formatSpan,
  peripheralPhaseLabel,
  slaDeadline,
  type Holiday,
  type PeripheralStage,
  type SlaDayKind,
  type SlaDepartment,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { stageColorOf } from '../claim/ClaimsBoard.js';
import { deskOf } from '../claim/desk.js';
import { phaseLabel } from '../claim/labels.js';

/**
 * O nome do departamento e da etapa na configuração. Periféricos (15/10/2026)
 * não é mesa do sinistro (`deskOf`): o nome e a cor vêm do catálogo dele.
 */
const slaDesk = (d: SlaDepartment) =>
  d === 'perifericos' ? { name: 'Periféricos', of: 'de Periféricos' } : deskOf(d);
const slaPhaseLabel = (d: SlaDepartment, phase: string) =>
  d === 'perifericos'
    ? peripheralPhaseLabel(phase as PeripheralStage)
    : phaseLabel(d, phase);
const slaStageColor = (d: SlaDepartment, phase: string) =>
  d === 'perifericos'
    ? PERIPHERAL_STAGE_COLORS[phase as keyof typeof PERIPHERAL_STAGE_COLORS]
    : stageColorOf(d, phase);
import {
  useHolidays,
  usePhaseDeadlines,
  useSetPhaseDeadlines,
  useSlaSummary,
} from '../claim/useDeadlines.js';
import { HolidaysPanel } from './HolidaysPanel.js';

/**
 * Sinistros › Configuração › SLA das fases (07/10/2026).
 *
 * Quanto tempo o card pode ficar em cada etapa de trabalho, por departamento —
 * uma aba para cada, e uma tabela de largura cheia com uma linha por etapa: o
 * tempo em horas ou dias, atalhos, quando expira quem entrar agora e quantos
 * cards estão com o SLA expirado nela hoje.
 *
 * **Vale para quem entrar na fase depois de salvar.** O prazo é copiado para
 * a passagem quando o card entra (`claims_phase_passages.deadline_at`); quem
 * já está na fase continua com o prazo com que entrou. A barra de salvar diz
 * isso, porque é a pergunta que todo gestor faz ao mudar um número.
 *
 * Cada etapa conta em dias CORRIDOS, como no Pipefy, ou em dias ÚTEIS
 * (15/10/2026) — que pulam o fim de semana e os feriados da praça, cadastrados
 * na aba Feriados. Em dias úteis o SLA é em dias inteiros: ninguém decidiu o
 * expediente. Campo vazio é etapa sem SLA — a tela não inventa prazo.
 */
export function PhaseDeadlinesScreen() {
  const { data: context } = useSessionContext();
  const deadlines = usePhaseDeadlines();
  const [tab, setTab] = useState<SlaDepartment | 'feriados'>('analise');
  const holidays = useHolidays();

  return (
    <Page measure="cheia">
      <PageHeader title="SLA das fases">
        Quanto tempo um sinistro de {context?.currentTenant.nome} pode ficar em cada
        etapa. Passou disso, o card mostra o SLA expirado no quadro, na tabela e na mesa.
      </PageHeader>
      {deadlines.isError && <Alert tone="erro">{deadlines.error.message}</Alert>}
      {deadlines.data && (
        <Tabs value={tab} onValueChange={(v) => setTab(v as SlaDepartment | 'feriados')}>
          <TabsList>
            {SLA_DEPARTMENTS.map((d) => (
              <TabsTrigger key={d} value={d}>
                {slaDesk(d)?.name ?? d}
              </TabsTrigger>
            ))}
            <TabsTrigger value="feriados">Feriados</TabsTrigger>
          </TabsList>
          {SLA_DEPARTMENTS.map((d) => (
            <TabsContent key={d} value={d} className="mt-5">
              <DepartmentDeadlines
                department={d}
                rows={deadlines.data.filter((r) => r.department === d)}
                holidays={(holidays.data ?? []).map((h) => ({
                  date: h.date,
                  recurring: h.recurring,
                }))}
              />
            </TabsContent>
          ))}
          <TabsContent value="feriados" className="mt-5">
            <HolidaysPanel />
          </TabsContent>
        </Tabs>
      )}
    </Page>
  );
}

type Unit = 'h' | 'd';

/** O que o campo mostra: o número na unidade escolhida, a unidade e como conta. */
interface Entry {
  value: string;
  unit: Unit;
  dayKind: SlaDayKind;
}

/* Os atalhos de um clique — os SLAs que a operação mais usa. */
const PRESETS: readonly { label: string; spoken: string; hours: number }[] = [
  { label: '4h', spoken: '4 horas', hours: 4 },
  { label: '1 dia', spoken: '1 dia', hours: 24 },
  { label: '2 dias', spoken: '2 dias', hours: 48 },
  { label: '1 sem.', spoken: '1 semana', hours: 168 },
];

/**
 * Horas inteiras em dia cheio viram dias no campo; o resto fica em horas. Em
 * dias úteis, sempre dias.
 */
function entryOf(hours: number | null, dayKind: SlaDayKind = 'corridos'): Entry {
  const unit: Unit = dayKind === 'uteis' ? 'd' : 'h';
  if (hours === null) return { value: '', unit, dayKind };
  if (hours >= 24 && hours % 24 === 0) {
    return { value: String(hours / 24), unit: 'd', dayKind };
  }
  return { value: String(hours), unit: 'h', dayKind };
}

/** O campo de volta em horas; `null` é vazio, `NaN` é inválido. */
function hoursOf(entry: Entry): number | null {
  if (entry.value.trim() === '') return null;
  const n = Number(entry.value.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return Number.NaN;
  /* Em dias úteis não há hora quebrada (15/10/2026). */
  if (entry.dayKind === 'uteis' && (entry.unit !== 'd' || !Number.isInteger(n))) {
    return Number.NaN;
  }
  const hours = entry.unit === 'd' ? n * 24 : n;
  return Number.isInteger(hours) ? hours : Number.NaN;
}

function problemOf(entry: Entry): string | null {
  const hours = hoursOf(entry);
  if (hours === null) return null;
  if (Number.isNaN(hours)) {
    if (entry.dayKind === 'uteis') return 'Em dias úteis, use dias inteiros.';
    return entry.unit === 'd'
      ? 'Use dias inteiros ou meio dia (0,5).'
      : 'Use horas inteiras, maiores que zero.';
  }
  if (hours > MAX_DEADLINE_HOURS) return 'No máximo um ano.';
  return null;
}

const WHEN = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function DepartmentDeadlines({
  department,
  rows,
  holidays,
}: {
  department: SlaDepartment;
  rows: PhaseDeadline[];
  /** Os feriados da praça — a prévia em dias úteis conta com eles. */
  holidays: readonly Holiday[];
}) {
  const save = useSetPhaseDeadlines();
  const expired = useSlaSummary(department, 0);
  const desk = slaDesk(department);
  const initial = useMemo(
    () =>
      Object.fromEntries(rows.map((r) => [r.phase, entryOf(r.deadlineHours, r.dayKind)])),
    [rows],
  );
  const [draft, setDraft] = useState<Record<string, Entry>>(initial);
  const [saved, setSaved] = useState(false);
  /* Depois de salvar, a lista relida vira o novo ponto de partida. */
  useEffect(() => setDraft(initial), [initial]);

  const entry = (phase: string): Entry =>
    draft[phase] ?? { value: '', unit: 'h', dayKind: 'corridos' };
  const set = (phase: string, next: Entry) => {
    setSaved(false);
    save.reset();
    setDraft({ ...draft, [phase]: next });
  };
  const hoursFor = (phase: string) => {
    const h = hoursOf(entry(phase));
    return h === null || Number.isNaN(h) ? null : h;
  };
  const isChanged = (r: PhaseDeadline) => {
    const h = hoursOf(entry(r.phase));
    return (
      !Number.isNaN(h) && (h !== r.deadlineHours || entry(r.phase).dayKind !== r.dayKind)
    );
  };
  const changed = rows.filter(isChanged);
  const hasProblem = rows.some((r) => problemOf(entry(r.phase)) !== null);
  const dirty = rows.some((r) => {
    const a = entry(r.phase);
    const b = initial[r.phase]!;
    return (
      a.value !== b.value ||
      a.dayKind !== b.dayKind ||
      (a.value !== '' && a.unit !== b.unit)
    );
  });
  const total = rows.reduce((sum, r) => sum + (hoursFor(r.phase) ?? 0), 0);
  const withSla = rows.filter((r) => hoursFor(r.phase) !== null).length;
  const now = new Date();

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (hasProblem || changed.length === 0) return;
        save.mutate(
          {
            department,
            phases: changed.map((r) => ({
              phase: r.phase,
              deadlineHours: hoursOf(entry(r.phase)),
              dayKind: entry(r.phase).dayKind,
            })),
          },
          { onSuccess: () => setSaved(true) },
        );
      }}
    >
      <Card
        title={`${desk?.name ?? department} · ${withSla} de ${rows.length} etapas com SLA`}
        descricao={
          total > 0
            ? `Somando as etapas com SLA, ${formatSpan(total * 3_600_000)} do início ao fim ${desk?.of ?? ''}. Para tirar o SLA de uma etapa, apague o número.`
            : 'Nenhuma etapa com SLA ainda. Etapa com o campo vazio não tem SLA.'
        }
        bleed
      >
        <Table
          paginate={false}
          label={`SLA das etapas ${desk?.of ?? ''}`}
          items={rows}
          rowKey={(r) => r.phase}
          rowTitle={(r) => slaPhaseLabel(department, r.phase)}
          empty="Este departamento não tem etapas."
          columns={[
            {
              title: 'Etapa',
              alignment: 'inicio' as const,
              minWidth: '12rem',
              cell: (r) => (
                <span className="flex min-w-0 items-center gap-3">
                  <span className="w-5 shrink-0 text-xs font-semibold tabular-nums text-texto-tenue">
                    {rows.indexOf(r) + 1}
                  </span>
                  <span className="min-w-0 font-medium">
                    <KanbanStageLabel
                      stage={{
                        name: slaPhaseLabel(department, r.phase),
                        color: slaStageColor(department, r.phase),
                      }}
                    />
                  </span>
                  {isChanged(r) && <Badge tone="informacao">alterado</Badge>}
                </span>
              ),
            },
            {
              title: 'Contagem',
              alignment: 'inicio' as const,
              minWidth: '11rem',
              cell: (r) => (
                <DayKindToggle
                  name={slaPhaseLabel(department, r.phase)}
                  value={entry(r.phase).dayKind}
                  onChange={(dayKind) => {
                    const current = entry(r.phase);
                    const hours = hoursOf(current);
                    /* Para dias úteis, o número passa a ser em dias inteiros,
                       arredondado para cima: 36 horas viram 2 dias. */
                    set(
                      r.phase,
                      dayKind === 'uteis' && current.unit === 'h'
                        ? {
                            value:
                              hours && !Number.isNaN(hours)
                                ? String(Math.ceil(hours / 24))
                                : '',
                            unit: 'd',
                            dayKind,
                          }
                        : { ...current, dayKind },
                    );
                  }}
                />
              ),
            },
            {
              title: 'Tempo máximo na etapa',
              alignment: 'inicio' as const,
              minWidth: '12rem',
              cell: (r) => (
                <SlaInput
                  onlyDays={entry(r.phase).dayKind === 'uteis'}
                  id={`sla-${department}-${r.phase}`}
                  name={slaPhaseLabel(department, r.phase)}
                  entry={entry(r.phase)}
                  onChange={(next) => set(r.phase, next)}
                />
              ),
            },
            {
              title: 'Atalhos',
              alignment: 'inicio' as const,
              minWidth: '13rem',
              cell: (r) => (
                <span className="flex gap-1">
                  {PRESETS.filter(
                    (p) => entry(r.phase).dayKind !== 'uteis' || p.hours % 24 === 0,
                  ).map((p) => {
                    const active = hoursFor(r.phase) === p.hours;
                    return (
                      <button
                        key={p.label}
                        type="button"
                        aria-pressed={active}
                        aria-label={`${p.spoken} em ${slaPhaseLabel(department, r.phase)}`}
                        onClick={() =>
                          set(r.phase, entryOf(p.hours, entry(r.phase).dayKind))
                        }
                        className={cn(
                          'rounded-pilula border px-2 py-1 text-xs font-medium whitespace-nowrap transition-colors',
                          active
                            ? 'border-transparent bg-chrome-alto text-chrome-texto'
                            : 'border-borda text-texto-suave hover:border-texto-suave hover:text-texto',
                        )}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </span>
              ),
            },
            {
              title: 'Quem entrar agora expira',
              alignment: 'inicio' as const,
              minWidth: '9rem',
              cell: (r) => {
                const h = hoursFor(r.phase);
                const until = h
                  ? slaDeadline(now, h, entry(r.phase).dayKind, holidays)
                  : null;
                return until ? (
                  <span className="tabular-nums">{WHEN.format(until)}</span>
                ) : (
                  <span className="text-texto-tenue">sem prazo</span>
                );
              },
            },
            {
              title: 'SLA expirado agora',
              minWidth: '7rem',
              cell: (r) => {
                const n = expired.data?.find((e) => e.phase === r.phase)?.slaExpired ?? 0;
                return n > 0 ? (
                  <Badge tone="erro">{n === 1 ? '1 card' : `${n} cards`}</Badge>
                ) : (
                  <span className="text-texto-tenue">nenhum</span>
                );
              },
            },
          ]}
        />
      </Card>

      {/* A barra de salvar fica no pé enquanto a tela rola. */}
      <div
        className={cardClasses({
          density: 'densa',
          className:
            'sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3',
        })}
      >
        <p className="text-xs text-texto-suave" role="status">
          {save.isError
            ? save.error.message
            : saved
              ? 'Salvo. Vale para quem entrar na etapa daqui em diante.'
              : changed.length > 0
                ? `${changed.length === 1 ? '1 etapa alterada' : `${changed.length} etapas alteradas`}. Vale para quem entrar depois de salvar — quem já está mantém o prazo com que entrou.`
                : 'Em dias corridos ou úteis — os úteis pulam o fim de semana e os feriados da aba Feriados. Vale para quem entrar na etapa depois de salvar.'}
        </p>
        <div className="flex gap-2">
          {dirty && (
            <Button
              type="button"
              variant="secundario"
              disabled={save.isPending}
              onClick={() => {
                setDraft(initial);
                setSaved(false);
              }}
            >
              Desfazer
            </Button>
          )}
          <Button
            type="submit"
            loading={save.isPending}
            disabled={hasProblem || changed.length === 0}
          >
            Salvar
          </Button>
        </div>
      </div>
    </form>
  );
}

/** O número e a unidade, lado a lado, e o erro embaixo. */
function SlaInput({
  id,
  name,
  entry,
  onChange,
  onlyDays,
}: {
  id: string;
  name: string;
  entry: Entry;
  onChange: (next: Entry) => void;
  /** Em dias úteis, só a unidade dias. */
  onlyDays: boolean;
}) {
  const problem = problemOf(entry);
  return (
    <span className="flex flex-col gap-1">
      <span className="flex items-stretch gap-2">
        <label htmlFor={id} className="sr-only">
          Tempo máximo na etapa {name}
        </label>
        <input
          id={id}
          inputMode="decimal"
          placeholder="Sem SLA"
          value={entry.value}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-erro` : undefined}
          onChange={(e) =>
            onChange({ ...entry, value: e.target.value.replace(/[^\d,]/g, '') })
          }
          className={cn(
            'h-9 w-24 rounded-borda border bg-superficie px-3 text-sm font-semibold tabular-nums text-texto',
            'placeholder:font-normal placeholder:text-texto-tenue hover:border-texto-suave',
            'disabled:cursor-not-allowed disabled:opacity-60',
            problem ? 'border-erro' : 'border-borda-forte',
          )}
        />
        <span
          role="group"
          aria-label={`Unidade de ${name}`}
          className="flex shrink-0 overflow-hidden rounded-borda border border-borda-forte"
        >
          {(onlyDays ? (['d'] as const) : (['h', 'd'] as const)).map((u) => (
            <button
              key={u}
              type="button"
              aria-pressed={entry.unit === u}
              onClick={() => onChange({ ...entry, unit: u })}
              className={cn(
                'px-3 text-xs font-semibold transition-colors',
                entry.unit === u
                  ? 'bg-chrome-alto text-chrome-texto'
                  : 'bg-superficie text-texto-suave hover:text-texto',
              )}
            >
              {u === 'h' ? 'horas' : 'dias'}
            </button>
          ))}
        </span>
      </span>
      {problem && (
        <span id={`${id}-erro`} className="text-xs text-erro">
          {problem}
        </span>
      )}
    </span>
  );
}

/** Dias corridos ou úteis, lado a lado — a mesma peça da unidade. */
function DayKindToggle({
  name,
  value,
  onChange,
}: {
  name: string;
  value: SlaDayKind;
  onChange: (next: SlaDayKind) => void;
}) {
  return (
    <span
      role="group"
      aria-label={`Contagem de ${name}`}
      className="flex w-fit shrink-0 overflow-hidden rounded-borda border border-borda-forte"
    >
      {SLA_DAY_KINDS.map((k) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          className={cn(
            'h-9 px-3 text-xs font-semibold whitespace-nowrap transition-colors',
            value === k
              ? 'bg-chrome-alto text-chrome-texto'
              : 'bg-superficie text-texto-suave hover:text-texto',
          )}
        >
          {SLA_DAY_KIND_LABEL[k]}
        </button>
      ))}
    </span>
  );
}
